import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { MapPin, Send, ImagePlus, X } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import AIContentBlock from "@/components/AIContentBlock";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const KINDS = [
  { value: "story", label: "Share a Story", desc: "A local music memory, tradition or personal journey." },
  { value: "event", label: "Submit an Event", desc: "A festival, concert or community session near you." },
  { value: "venue", label: "Suggest a Venue", desc: "A live venue, studio, session pub or music school worth documenting." },
  { value: "correction", label: "Suggest a Correction", desc: "Help us fix missing or inaccurate information." },
];

const TAB_TOPICS = {
  story: "Sharing personal music stories, memories and oral histories with a global community",
  event: "Submitting local concerts, festivals and community music events for editorial review",
  venue: "Suggesting local music venues, studios and session spots for the community directory",
  correction: "Suggesting corrections and source-backed fixes to music records and profiles",
};

const empty = { name: "", email: "", location: "", title: "", body: "", media_link: "" };

export default function Community() {
  const [stories, setStories] = useState([]);
  const [kind, setKind] = useState("story");
  const [form, setForm] = useState(empty);
  const [photo, setPhoto] = useState(null);
  const [sending, setSending] = useState(false);
  const [params] = useSearchParams();

  useEffect(() => {
    api.get("/stories").then((r) => setStories(r.data)).catch(console.error);
  }, []);

  useEffect(() => {
    const k = params.get("kind");
    if (k && KINDS.some((x) => x.value === k)) setKind(k);
    const loc = params.get("location");
    if (loc) setForm((f) => ({ ...f, location: loc }));
  }, [params]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.title || !form.body) {
      toast.error("Please fill in your name, a title and your story.");
      return;
    }
    setSending(true);
    try {
      let photo_file_id;
      if (kind === "venue" && photo) {
        const fd = new FormData();
        fd.append("file", photo);
        const up = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 120000 });
        photo_file_id = up.data.file_id;
      }
      const extra = kind === "venue"
        ? { city_slug: params.get("city_slug") || undefined, country_slug: params.get("country_slug") || undefined, city_name: params.get("city_name") || undefined, photo_file_id }
        : {};
      await api.post("/contributions", { ...form, ...extra, kind });
      toast.success("Thank you! Your contribution is pending review by our editorial team.");
      setForm(empty);
      setPhoto(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setSending(false);
    }
  };

  const inputCls = "w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-pink-400 focus:bg-white transition-all";

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid="community-page">
      <p className="text-xs font-bold uppercase tracking-[0.3em] text-slate-500 mb-3">Community &amp; Participation</p>
      <h1 className="font-display text-4xl sm:text-5xl font-black text-slate-900">Every Place Has a Story. <span className="gradient-text">Tell Yours.</span></h1>
      <p className="mt-3 text-slate-600 max-w-2xl">Real people. Real places. Real connections through music. All submissions are reviewed with attribution, consent and edit history preserved.</p>

      <AIContentBlock pageKey="community" topic="Community music stories, shared memory and participation" imgQuery="people singing together community music" />

      <div className="grid lg:grid-cols-5 gap-10 mt-10">
        <div className="lg:col-span-3 grid sm:grid-cols-2 gap-6">
          {stories.map((s) => (
            <div key={s.id} className="card-lift bg-white rounded-3xl border border-slate-100 overflow-hidden" data-testid={`story-card-${s.id}`}>
              <div className="h-36 overflow-hidden"><img src={s.image} alt="" className="w-full h-full object-cover" /></div>
              <div className="p-5">
                <h2 className="font-display text-lg font-black text-slate-900 leading-snug">{s.title}</h2>
                <p className="text-xs text-slate-500 flex items-center gap-1 mt-1.5"><MapPin className="w-3 h-3 text-pink-500" /> {s.author} · {s.location}</p>
                <p className="text-sm text-slate-600 mt-3">{s.body}</p>
                <span className="inline-block mt-3 px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-700 text-[10px] font-black uppercase tracking-wide">Approved · Community voice</span>
              </div>
            </div>
          ))}
        </div>

        <div className="lg:col-span-2">
          <div className="bg-white rounded-3xl border border-slate-100 shadow-lg shadow-slate-200/50 p-6 sticky top-24">
            <h2 className="font-display text-2xl font-black text-slate-900 mb-1">Contribute</h2>
            <p className="text-xs text-slate-500 mb-5">Submissions stay pending until reviewed. No account needed.</p>
            <Tabs value={kind} onValueChange={setKind}>
              <TabsList className="grid grid-cols-4 w-full rounded-full">
                {KINDS.map((k) => (
                  <TabsTrigger key={k.value} value={k.value} data-testid={`contrib-tab-${k.value}`} className="rounded-full text-xs">{k.label.split(" ").slice(-1)[0]}</TabsTrigger>
                ))}
              </TabsList>
              {KINDS.map((k) => (
                <TabsContent key={k.value} value={k.value}>
                  <p className="text-xs text-slate-500 mb-1">{k.desc}</p>
                </TabsContent>
              ))}
            </Tabs>
            <AIContentBlock pageKey={`community-tab-${kind}`} topic={TAB_TOPICS[kind]} compact />
            <form onSubmit={submit} className="space-y-3" data-testid="contribution-form">
              <div className="grid grid-cols-2 gap-3">
                <input data-testid="contrib-name-input" value={form.name} onChange={set("name")} placeholder="Your name *" className={inputCls} />
                <input data-testid="contrib-email-input" value={form.email} onChange={set("email")} placeholder="Email (optional)" className={inputCls} />
              </div>
              <input data-testid="contrib-location-input" value={form.location} onChange={set("location")} placeholder="Location (city, country)" className={inputCls} />
              <input data-testid="contrib-title-input" value={form.title} onChange={set("title")} placeholder="Title *" className={inputCls} />
              <textarea data-testid="contrib-body-input" value={form.body} onChange={set("body")} placeholder={kind === "correction" ? "What should be corrected, and what's your source? *" : "Tell us your story... *"} rows={5} className={inputCls} />
              <input data-testid="contrib-media-input" value={form.media_link} onChange={set("media_link")} placeholder="Link to photo/audio/video (optional)" className={inputCls} />
              {kind === "venue" && (
                <div data-testid="contrib-photo-section">
                  {photo ? (
                    <div className="flex items-center gap-3 bg-slate-50 rounded-xl border border-slate-200 p-2.5">
                      <img src={URL.createObjectURL(photo)} alt="Venue preview" className="w-14 h-14 rounded-lg object-cover" data-testid="contrib-photo-preview" />
                      <div className="min-w-0 flex-1">
                        <p className="text-xs font-bold text-slate-800 truncate">{photo.name}</p>
                        <p className="text-[10px] text-slate-400">{(photo.size / 1024 / 1024).toFixed(1)} MB</p>
                      </div>
                      <button type="button" data-testid="contrib-photo-remove" onClick={() => setPhoto(null)} className="p-1.5 rounded-full hover:bg-rose-50 text-rose-500"><X className="w-4 h-4" /></button>
                    </div>
                  ) : (
                    <label className="flex items-center gap-2 rounded-xl border-2 border-dashed border-slate-200 px-4 py-3 text-xs font-bold text-slate-500 hover:border-pink-300 hover:text-pink-600 cursor-pointer transition-colors" data-testid="contrib-photo-label">
                      <ImagePlus className="w-4 h-4" /> Add a photo of the venue (optional, max 5MB)
                      <input
                        type="file"
                        accept="image/jpeg,image/png,image/webp,image/gif"
                        className="hidden"
                        data-testid="contrib-photo-input"
                        onChange={(e) => {
                          const f = e.target.files?.[0];
                          if (!f) return;
                          if (f.size > 5 * 1024 * 1024) { toast.error("Image must be under 5MB."); return; }
                          setPhoto(f);
                        }}
                      />
                    </label>
                  )}
                </div>
              )}
              <button type="submit" disabled={sending} data-testid="contrib-submit-btn" className="gradient-btn w-full text-white font-bold px-6 py-3 rounded-full inline-flex items-center justify-center gap-2 disabled:opacity-60">
                <Send className="w-4 h-4" /> {sending ? "Submitting..." : "Submit for Review"}
              </button>
              <p className="text-[10px] text-slate-400 leading-relaxed">By submitting you consent to editorial review and attribution. We never publish private allegations or sensitive personal data.</p>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
